import { ShieldCheck } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ModeDot } from '@/components/ModeSwitcher';
import type { AllowedEnvironments } from '@/lib/mode/ModeProvider';
import { cn } from '@/lib/utils';

interface EnvironmentAccessCardProps {
  allowed: AllowedEnvironments;
  /** Copy varies between admin ("a SuperAdmin") and portal ("your
   *  ClientAdmin"); pass whichever applies. */
  contact: string;
}

/**
 * Read-only summary of the user's `AllowedEnvironments` entitlement.
 * Shows the two environments as coloured chips — solid when granted,
 * outlined + muted when not. Matches the lightweight density of the
 * surrounding profile cards.
 */
export function EnvironmentAccessCard({ allowed, contact }: EnvironmentAccessCardProps) {
  const hasProd = allowed === 'Production' || allowed === 'Both';
  const hasSandbox = allowed === 'Sandbox' || allowed === 'Both';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck aria-hidden className="h-4 w-4 text-muted-foreground" />
          Environment access
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <EnvChip mode="production" granted={hasProd} />
          <EnvChip mode="sandbox" granted={hasSandbox} />
        </div>
        <p className="text-xs text-muted-foreground">
          Contact {contact} to change your environment access.
        </p>
      </CardContent>
    </Card>
  );
}

interface EnvChipProps {
  mode: 'production' | 'sandbox';
  granted: boolean;
}

function EnvChip({ mode, granted }: EnvChipProps) {
  const label = mode === 'production' ? 'Production' : 'Sandbox';
  return (
    <span
      data-testid={`env-chip-${mode}`}
      data-granted={granted ? 'true' : 'false'}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        granted
          ? 'border-transparent bg-muted text-foreground'
          : 'border-dashed border-border/60 text-muted-foreground/60',
      )}
    >
      <ModeDot mode={mode} className={granted ? undefined : 'opacity-40'} />
      <span>{label}</span>
      {!granted ? <span className="text-[10px] uppercase tracking-wide">No access</span> : null}
    </span>
  );
}
